// Forwards every query parameter on the landing URL into the outgoing lead
// payload.
//
// Traffic sources each use their own naming (pubid vs mPubID, subid1 vs sub1,
// clickid, mpartner, utm_*, ...). Rather than maintaining an alias list that
// silently drops anything unrecognized, we pass the whole query string through
// and let the receiving system read whichever keys it knows.
//
// Merge this UNDER the payload's own computed fields, so a form answer always
// beats a same-named URL value.

// Every key is forwarded, including unsubstituted macros like "[mPubID]" or
// "{campaignid}". They are junk data, but seeing them arrive is how you tell a
// broken traffic-source link apart from a broken integration — so the value is
// passed on rather than silently dropped.
export function getUrlPassthroughParams(
  search: string,
): Record<string, string> {
  const out: Record<string, string> = {}
  const params = new URLSearchParams(search)
  params.forEach((value, key) => {
    if (!key) return
    // Last value wins for repeated keys, matching URLSearchParams.get().
    out[key] = value.trim()
  })
  return out
}

// Publisher identification is resolved by getPublisherParams(), which enforces
// "either mPubGroupID or the mPubID + mPubCampaignID pair, never a mix". Left
// to the generic pass-through, a URL carrying all three would leak the losing
// keys into the payload, so they are excluded here.
const PUBLISHER_KEYS = ['mPubGroupID', 'mPubID', 'mPubCampaignID']

// Consent certification must only ever come from the Jornaya / TrustedForm
// scripts running on THIS page visit. An incoming URL value is either an
// unsubstituted macro ("[trustedFormUrl]") or a token minted somewhere else —
// both are worse than sending nothing, because they look like certification
// while proving nothing. Never let the pass-through supply them.
const CERTIFICATION_KEYS = [
  'leadIdToken',
  'trustedFormUrl',
  'trustedFormPingUrl',
]

// Adds every URL query parameter to an outgoing request, without clobbering
// values the form already resolved. A key is filled only when the request has
// no value for it yet, so a form answer (or a deliberately computed value such
// as sub3's Meta match keys) always wins over the same-named URL param.
export function applyUrlPassthrough(
  target: URLSearchParams,
  search: string,
): void {
  const passthrough = getUrlPassthroughParams(search)
  Object.entries(passthrough).forEach(([key, value]) => {
    if (!value) return
    if (PUBLISHER_KEYS.includes(key)) return
    if (CERTIFICATION_KEYS.includes(key)) return
    if (target.get(key)) return
    target.set(key, value)
  })
}
